import { z } from 'zod';
import { normalizePhone, todayInDhaka } from '../format.js';
import { MAX_MONEY } from '../salary.js';
/**
 * Field-level building blocks implementing SRS §9 validation rules. Used by API DTOs
 * (nestjs-zod) and react-hook-form resolvers alike.
 */
/** UUID v7 primary key reference (ADR-023). Ids are never client-supplied on create. */
export const uuid = z.uuid();
export const idParamSchema = z.object({ id: uuid });
/** Calendar date `YYYY-MM-DD` (BR-2); rejects impossible dates such as 2026-02-30. */
export const isoDate = z.iso.date();
/** Trimmed, lower-cased, RFC-valid email. */
export const email = z.string().trim().toLowerCase().max(254).pipe(z.email());
/** Person or contact name: 2–120 characters. */
export const personName = z.string().trim().min(2).max(120);
/** Free-text field; blank input becomes `null`. `undefined` (omitted) is kept for PATCH. */
export function optionalText(max) {
    return z
        .string()
        .trim()
        .max(max)
        .nullish()
        .transform((v) => (v === '' ? null : v));
}
/** BD mobile number, normalised to `01XXXXXXXXX` (SRS §9, FR-SELF-1). */
export const phone = z.union([z.string(), z.number()]).transform((value, ctx) => {
    const normalized = normalizePhone(value);
    if (normalized === null) {
        ctx.issues.push({
            code: 'custom',
            input: value,
            message: 'Enter a Bangladeshi mobile number, e.g. 01712345678',
        });
        return z.NEVER;
    }
    return normalized;
});
/** Whole taka ≥ 0 (BR-SAL-1..5, ADR-011). */
export const money = z.number().int().min(0).max(MAX_MONEY);
/** Whole taka > 0 (gross salary). */
export const positiveMoney = money.min(1);
/** NID / Birth ID: digits, length 10, 13 or 17. */
export const nidNumber = z
    .string()
    .trim()
    .regex(/^(\d{10}|\d{13}|\d{17})$/, 'NID / Birth ID must be 10, 13 or 17 digits');
/** Passport number: letters and digits, stored upper-case. */
export const passportNumber = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9]{6,20}$/, 'Passport number must be 6–20 letters or digits');
/** TIN: 12 digits (FR-BANK-1). */
export const tin = z
    .string()
    .trim()
    .regex(/^\d{12}$/, 'TIN must be 12 digits');
/** Bank account number: 10–20 digits (FR-BANK-1). */
export const accountNumber = z
    .string()
    .trim()
    .regex(/^\d{10,20}$/, 'Account number must be 10–20 digits');
/** Profile URL; `https://` is added when the scheme is missing (IMPORT_MAPPING §2.2). */
export const profileUrl = z
    .string()
    .trim()
    .max(300)
    .transform((v) => (/^[a-z][a-z\d+.-]*:\/\//i.test(v) ? v : `https://${v}`))
    .pipe(z.url({ protocol: /^https?$/ }));
/** Whole years between two ISO dates (`dob` → `on`). */
export function ageOn(dob, on) {
    const [by = 0, bm = 0, bd = 0] = dob.split('-').map(Number);
    const [y = 0, m = 0, d = 0] = on.split('-').map(Number);
    return y - by - (m < bm || (m === bm && d < bd) ? 1 : 0);
}
/** Date of birth: age 16–80 today in Asia/Dhaka (SRS §9). */
export const dateOfBirth = isoDate.refine((dob) => {
    const age = ageOn(dob, todayInDhaka());
    return age >= 16 && age <= 80;
}, { message: 'Age must be between 16 and 80' });
/** Query-string boolean: `true`/`false`/`1`/`0`. */
export const queryBoolean = z
    .enum(['true', 'false', '1', '0'])
    .transform((v) => v === 'true' || v === '1');
/** Accepts `?x=a&x=b` and `?x=a,b`. */
export function queryList(item) {
    return z.preprocess((value) => {
        if (typeof value === 'string')
            return value.split(',').filter((s) => s !== '');
        return value;
    }, z.array(item));
}
export const DEFAULT_PAGE_SIZE = 25;
export const MAX_PAGE_SIZE = 100;
/** `?page=&pageSize=` (SRS §7: max 100). */
export const paginationQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});
/** Paginated response envelope `{items,total,page,pageSize}`. */
export function paginated(item) {
    return z.object({
        items: z.array(item),
        total: z.number().int().min(0),
        page: z.number().int().min(1),
        pageSize: z.number().int().min(1),
    });
}
export const sortOrder = z.enum(['asc', 'desc']);
/** Reason text for voids, removals and rejections. */
export const reasonText = z.string().trim().min(3).max(500);
export const remarks = optionalText(1000);
//# sourceMappingURL=common.js.map